// Self-hosted replacement for gatsby-plugin-google-fonts (unpublished since
// May 2022): same weights/styles the old config requested, served from our
// own assets instead of a third-party request to Google Fonts on every load.
// `latin` subset only: all site content is Dutch (`lang="nl"`, see
// gatsby-ssr.js), which only needs Latin Basic + Latin-1 Supplement.
import '@fontsource/titillium-web/latin-200.css';
import '@fontsource/titillium-web/latin-200-italic.css';
import '@fontsource/titillium-web/latin-300.css';
import '@fontsource/titillium-web/latin-300-italic.css';
import '@fontsource/titillium-web/latin-400.css';
import '@fontsource/titillium-web/latin-400-italic.css';
import '@fontsource/titillium-web/latin-600.css';
import '@fontsource/titillium-web/latin-600-italic.css';
import '@fontsource/titillium-web/latin-700.css';
import '@fontsource/titillium-web/latin-700-italic.css';

import './src/styles/global.scss';
import 'bootstrap/dist/js/bootstrap.min.js'

import { initCookieConsent } from './src/utils/consent';

export const onClientEntry = () => {
  initCookieConsent();
};

// Guards against a stale-scroll bug: src/components/unit/unit-subnav.tsx
// updates the URL hash on click via a plain window.history.replaceState()
// (so a page's own sticky sub-nav gets a shareable/bookmarkable URL without
// going through a full Gatsby route change for what's really just an
// in-page scroll). That raw History API call never goes through Gatsby's
// router, though, so its own scroll-position bookkeeping for the current
// entry can go stale — the *next* real navigation (e.g. via the
// Programma's dropdown to a different onderdeel's page) could then land at
// an unrelated leftover scroll position instead of the top of the new
// page. Force a real top-of-page start whenever the pathname actually
// changes; a hash-only change on the very same pathname (a same-page
// anchor jump) is left to the default behaviour, which is what lets
// gatsby-plugin-anchor-links do its own hash-based scroll.
export const shouldUpdateScroll = ({ routerProps: { location }, prevRouterProps }) => {
  const prevPathname = prevRouterProps && prevRouterProps.location && prevRouterProps.location.pathname;
  // Leave a destination hash alone (e.g. the Programma's dropdown jumping
  // from another page to /#orange-brass on the homepage) — that's a
  // legitimate cross-page anchor jump gatsby-plugin-anchor-links handles.
  // Its cross-page AnchorLink clicks (stripHash, see MenuItems/
  // ProgramsDropdown) navigate to a hash-less URL and instead stash the
  // real target in window.gatsby_scroll_hash for its own onRouteUpdate to
  // consume, so location.hash alone isn't enough to detect them.
  const hashTarget = location.hash || window.gatsby_scroll_hash;
  if (prevPathname && prevPathname !== location.pathname) {
    // Any real navigation invalidates a correction scheduled by a previous
    // one — otherwise it could keep watching after its own page is gone and
    // "correct" a same-named anchor on whatever page loads next.
    if (stopAnchorDriftCorrection) stopAnchorDriftCorrection();

    if (hashTarget) {
      // Read here rather than in the correction itself: by the time it
      // runs, gatsby-plugin-anchor-links' own onRouteUpdate (a separate,
      // unordered browser API from a different plugin) has already
      // consumed and cleared window.gatsby_scroll_hash.
      scheduleAnchorDriftCorrection(hashTarget.replace(/^#/, ''));
    } else {
      // An explicit `behavior: 'instant'` scroll, not Gatsby's own default
      // handling (returning `true`/an array and letting gatsby-react-router-
      // scroll call the bare two-argument `window.scrollTo(0, position)`):
      // that form inherits the site-wide `scroll-behavior: smooth` (Bootstrap
      // 5's $enable-smooth-scroll default in global.scss's bootstrap import),
      // which can lose a race against an already-in-flight smooth scroll —
      // e.g. clicking a page's own sticky sub-nav (unit-subnav.tsx) and then
      // immediately navigating away via the Programma's dropdown before that
      // scroll finishes — landing partway through the old animation instead
      // of at the top of the new page. `behavior: 'instant'` overrides CSS
      // and cancels any in-progress smooth scroll outright. Returning `false`
      // stops gatsby-react-router-scroll from then also doing its own
      // (CSS-affected) scroll on top of this one.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return false;
    }
  }

  return true;
};

// gatsby-plugin-anchor-links (see its options in gatsby-config.js, and
// node_modules/gatsby-plugin-anchor-links/utils.js + the scroll-to-element
// package it wraps) measures its target's position exactly once, right when
// the destination page mounts, and animates to that one fixed value over a
// fixed duration. A cross-page anchor jump into a low section (e.g. the
// Programma's dropdown, or the main nav's "Contact" item) lands short and
// stays there whenever the page keeps growing underneath the target after
// that: the homepage's #agenda section starts empty and is filled in by an
// async fetch (see EventsList's useEffect in src/components/events-list.tsx)
// well after the one-shot scroll has already finished, and image-heavy
// sections can shift similarly as their content settles.
//
// Fix: re-measure the intended target for a few seconds after the page
// mounts and correct the scroll position whenever the page has grown
// underneath it. A ResizeObserver on <body> means this only ever does work
// when the layout actually changes size, and it always yields to the
// visitor's own scrolling the moment they touch the wheel/trackpad/keyboard.
const ANCHOR_LINK_OFFSET = -72; // must match gatsby-config.js's gatsby-plugin-anchor-links `offset` option
const ANCHOR_DRIFT_TOLERANCE_PX = 4;
const ANCHOR_DRIFT_TIMEOUT_MS = 5000;

let stopAnchorDriftCorrection;

const scheduleAnchorDriftCorrection = (anchorId) => {
  if (stopAnchorDriftCorrection) stopAnchorDriftCorrection();
  if (typeof ResizeObserver === 'undefined') return;

  let resizeDebounce;

  const correctIfNeeded = () => {
    const el = document.getElementById(anchorId);
    if (!el) return;

    const doc = document.documentElement;
    const maxScrollY = Math.max(doc.scrollHeight, document.body.scrollHeight) - doc.clientHeight;
    const targetY = Math.max(0, Math.min(
      el.getBoundingClientRect().top + window.pageYOffset + ANCHOR_LINK_OFFSET,
      maxScrollY,
    ));

    if (Math.abs(targetY - window.pageYOffset) > ANCHOR_DRIFT_TOLERANCE_PX) {
      window.scrollTo({ top: targetY, left: 0, behavior: 'instant' });
    }
  };

  const stop = () => {
    clearTimeout(resizeDebounce);
    clearTimeout(timeoutId);
    observer.disconnect();
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
  };
  stopAnchorDriftCorrection = stop;

  const observer = new ResizeObserver(() => {
    clearTimeout(resizeDebounce);
    resizeDebounce = setTimeout(correctIfNeeded, 100);
  });
  observer.observe(document.body);

  // A visitor who starts scrolling themselves knows better than a leftover
  // correction from before they got involved.
  window.addEventListener('wheel', stop, { passive: true, once: true });
  window.addEventListener('touchstart', stop, { passive: true, once: true });
  window.addEventListener('keydown', stop, { once: true });

  const timeoutId = setTimeout(stop, ANCHOR_DRIFT_TIMEOUT_MS);
};

// Replaces gatsby-plugin-google-gtag's own onRouteUpdate (removed - it has
// no way to gate loading behind consent, see src/utils/consent.ts): fires a
// GA pageview on every route change, but only once analytics consent has
// actually been granted and gtag has loaded. Mirrors that plugin's own
// implementation (double rAF + setTimeout, to let the page title/react-head
// finish updating first) - see docs/TECH-NOTES.md.
export const onRouteUpdate = ({ location }) => {
  if (typeof window.gtag !== 'function') {
    return;
  }

  const sendPageView = () => {
    window.gtag('event', 'page_view', {
      page_path: location ? location.pathname + location.search + location.hash : undefined,
    });
  };

  if ('requestAnimationFrame' in window) {
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(sendPageView, 0)));
  } else {
    setTimeout(sendPageView, 32);
  }
};
